import * as THREE from 'three';
import { OhlecObjectiveDef, OhlecHazardDef, OhlecLeanDef, ScenarioDef } from '../types';
import { Renderer } from './Renderer';
import { Environment } from './Environment';
import { EntityManager } from './Entities';
import { updateObjectiveUI, updateLeanUI, showOhlecToast, setStep } from '../ui/ohlecHUD';

// Rule 1: Tunable constants defined at the top of the file in UPPER_SNAKE_CASE
const RETICLE_RADIUS_DEFAULT = 1.5;
const RETICLE_COLOR_NEUTRAL = 0x00ffff;
const RETICLE_COLOR_VALID = 0x00ff88;
const RETICLE_COLOR_INVALID = 0xff4444;
const GROUND_Y_OFFSET = 0.05;
const PLACED_MARKER_OPACITY = 0.6;
const PREVIEW_RETICLE_OPACITY = 0.45;

const HAZARD_SHAKE_DURATION = 0.4;
const HAZARD_SHAKE_FREQUENCY = 40;
const HAZARD_SHAKE_AMPLITUDE = 0.08;
const HAZARD_HIGHLIGHT_COLOR = 0xff0000;
const HAZARD_EMISSIVE_COLOR = 0x660000;

const LOG_HIGHLIGHT_COLOR = 0x00ff88;
const LOG_HIGHLIGHT_EMISSIVE = 0x004422;

interface ActiveShake {
  object: THREE.Object3D;
  initialPosition: THREE.Vector3;
  elapsed: number;
  duration: number;
}

export class OhlecManager {
  private currentStep: number = 1;
  private objectives: OhlecObjectiveDef[] = [];
  private currentObjectiveIndex: number = 0;

  private totalCriticalHazards: number = 0;
  private identifiedHazardIds: Set<string> = new Set();

  private leans: OhlecLeanDef[] = [];
  private currentLeanIndex: number = 0;

  private activeShakes: ActiveShake[] = [];

  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();

  private previewReticle: THREE.Mesh | null = null;
  private placedMarkers: THREE.Mesh[] = [];

  private domElement: HTMLElement | null = null;
  private boundPointerMove: (e: PointerEvent) => void;
  private boundPointerDown: (e: PointerEvent) => void;

  private active: boolean = false;

  constructor(
    private renderer: Renderer,
    private environment: Environment,
    private entityManager?: EntityManager
  ) {
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerDown = this.onPointerDown.bind(this);
  }

  public init(scenario: ScenarioDef) {
    this.dispose();

    this.active = true;
    this.currentStep = 1;
    this.currentObjectiveIndex = 0;
    this.placedMarkers = [];
    this.identifiedHazardIds.clear();
    this.activeShakes = [];

    // Parse & normalize scenario objectives
    if (scenario.ohlec?.objective) {
      if (Array.isArray(scenario.ohlec.objective)) {
        this.objectives = scenario.ohlec.objective;
      } else {
        this.objectives = [scenario.ohlec.objective];
      }
    } else {
      this.objectives = [];
    }

    // Parse & count critical hazards
    if (scenario.ohlec?.hazards) {
      this.totalCriticalHazards = scenario.ohlec.hazards.filter((h) => h.critical).length;
    } else {
      this.totalCriticalHazards = 0;
    }

    // Parse & count leans/binds
    if (scenario.ohlec?.leans) {
      this.leans = scenario.ohlec.leans;
    } else {
      this.leans = [];
    }
    this.currentLeanIndex = 0;

    // Create preview reticle mesh
    this.createPreviewReticle();

    // Bind DOM events
    this.domElement = this.renderer.renderer.domElement;
    this.domElement.addEventListener('pointermove', this.boundPointerMove);
    this.domElement.addEventListener('pointerdown', this.boundPointerDown);

    // Initialize HUD for Step 1
    setStep(1);
    this.syncHUDWithCurrentObjective();

    console.log(`[OhlecManager] Initialized with ${this.objectives.length} objective(s), ${this.totalCriticalHazards} critical hazard(s), and ${this.leans.length} lean(s).`);
  }

  private createPreviewReticle() {
    const geometry = new THREE.RingGeometry(0.2, RETICLE_RADIUS_DEFAULT, 32);
    geometry.rotateX(-Math.PI / 2); // Align horizontally with terrain

    const material = new THREE.MeshBasicMaterial({
      color: RETICLE_COLOR_NEUTRAL,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: PREVIEW_RETICLE_OPACITY,
      depthTest: true,
    });

    this.previewReticle = new THREE.Mesh(geometry, material);
    this.previewReticle.visible = false;
    this.renderer.scene.add(this.previewReticle);
  }

  private syncHUDWithCurrentObjective() {
    if (this.currentStep !== 1) return;

    if (this.objectives.length > 0 && this.currentObjectiveIndex < this.objectives.length) {
      const currentObj = this.objectives[this.currentObjectiveIndex];
      updateObjectiveUI(
        this.currentObjectiveIndex + 1,
        this.objectives.length,
        currentObj.description
      );

      // Scale reticle to match current target zone radius if specified
      if (this.previewReticle && currentObj.target_zone.radius) {
        const radius = currentObj.target_zone.radius;
        this.previewReticle.geometry.dispose();
        this.previewReticle.geometry = new THREE.RingGeometry(0.2, radius, 32);
        this.previewReticle.geometry.rotateX(-Math.PI / 2);
      }
    }
  }

  private syncHUDWithCurrentLean() {
    if (this.currentStep !== 3) return;

    if (this.leans.length > 0 && this.currentLeanIndex < this.leans.length) {
      const currentLean = this.leans[this.currentLeanIndex];
      updateLeanUI(
        this.currentLeanIndex + 1,
        this.leans.length,
        currentLean.bind_type
      );
    }
  }

  private onPointerMove(e: PointerEvent) {
    if (!this.active || this.currentStep !== 1 || !this.previewReticle) return;

    const groundMesh = this.environment.getGroundMesh();
    if (!groundMesh) return;

    const rect = this.renderer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.renderer.camera);
    const intersects = this.raycaster.intersectObject(groundMesh);

    if (intersects.length > 0) {
      const hitPoint = intersects[0].point;
      this.previewReticle.position.set(hitPoint.x, hitPoint.y + GROUND_Y_OFFSET, hitPoint.z);
      this.previewReticle.visible = true;

      // Color hint check
      if (this.currentObjectiveIndex < this.objectives.length) {
        const target = this.objectives[this.currentObjectiveIndex].target_zone;
        const dx = hitPoint.x - target.x;
        const dz = hitPoint.z - target.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        const mat = this.previewReticle.material as THREE.MeshBasicMaterial;
        if (dist <= target.radius) {
          mat.color.setHex(RETICLE_COLOR_VALID);
        } else {
          mat.color.setHex(RETICLE_COLOR_NEUTRAL);
        }
      }
    } else {
      this.previewReticle.visible = false;
    }
  }

  private onPointerDown(e: PointerEvent) {
    if (!this.active || e.button !== 0) return;

    if (this.currentStep === 1) {
      this.handleStep1PointerDown(e);
    } else if (this.currentStep === 2) {
      this.handleStep2PointerDown(e);
    } else if (this.currentStep === 3) {
      this.handleStep3PointerDown(e);
    }
  }

  private handleStep1PointerDown(e: PointerEvent) {
    const groundMesh = this.environment.getGroundMesh();
    if (!groundMesh) return;

    const rect = this.renderer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.renderer.camera);
    const intersects = this.raycaster.intersectObject(groundMesh);

    if (intersects.length === 0) return;

    const hitPoint = intersects[0].point;
    this.validateObjectiveClick(hitPoint.x, hitPoint.y, hitPoint.z);
  }

  private handleStep2PointerDown(e: PointerEvent) {
    const hazardMeshes = this.environment.getHazardMeshes();
    if (hazardMeshes.length === 0) return;

    const rect = this.renderer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.renderer.camera);
    const intersects = this.raycaster.intersectObjects(hazardMeshes, true);

    if (intersects.length === 0) return;

    const hitObj = intersects[0].object;
    const hazardDef: OhlecHazardDef | undefined = hitObj.userData.hazardDef;
    const hazardRoot: THREE.Object3D = hitObj.userData.hazardRoot || hitObj;

    if (!hazardDef) return;

    if (hazardDef.critical === true) {
      if (this.identifiedHazardIds.has(hazardDef.id)) {
        showOhlecToast('Critical hazard already identified!', true);
        return;
      }

      this.identifiedHazardIds.add(hazardDef.id);
      this.triggerShake(hazardRoot);
      this.highlightMeshRed(hazardRoot);

      showOhlecToast(`Critical Hazard Identified: ${(hazardDef.type || 'Hazard').toUpperCase()}!`, true);

      if (this.identifiedHazardIds.size >= this.totalCriticalHazards) {
        showOhlecToast('Step 2 Complete! All critical hazards identified.', true);
        if (this.leans.length > 0) {
          this.currentStep = 3;
          setStep(3);
          this.syncHUDWithCurrentLean();
        } else {
          this.currentStep = 4;
          setStep(4);
        }
      }
    } else {
      showOhlecToast('Non-critical item. Focus on identifying critical hazards!', false);
    }
  }

  private handleStep3PointerDown(e: PointerEvent) {
    if (!this.entityManager) return;

    const logEntities = this.entityManager.getAll().filter((ent) => ent.type === 'log' && ent.mesh);
    if (logEntities.length === 0) return;

    const logMeshes = logEntities.map((ent) => ent.mesh);

    const rect = this.renderer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.renderer.camera);
    const intersects = this.raycaster.intersectObjects(logMeshes, true);

    if (intersects.length === 0) return;

    const hitObj = intersects[0].object;
    const hitEntity = logEntities.find((ent) => {
      let curr: THREE.Object3D | null = hitObj;
      while (curr) {
        if (curr === ent.mesh) return true;
        curr = curr.parent;
      }
      return false;
    });

    if (!hitEntity) return;

    const logIndex = parseInt(hitEntity.id.split('-')[1]);
    if (isNaN(logIndex)) return;

    if (this.currentLeanIndex >= this.leans.length) return;

    const expectedLean = this.leans[this.currentLeanIndex];

    if (logIndex === expectedLean.log_index) {
      const formattedBind = expectedLean.bind_type.replace(/_/g, ' ').toUpperCase();
      showOhlecToast(`Identified ${formattedBind} on Log #${logIndex}!`, true);

      this.triggerShake(hitEntity.mesh);
      this.highlightMeshGreen(hitEntity.mesh);

      this.currentLeanIndex++;

      if (this.currentLeanIndex < this.leans.length) {
        this.syncHUDWithCurrentLean();
      } else {
        showOhlecToast('Step 3 Complete! All leans & binds identified.', true);
        this.clearLogHighlights();
        this.currentStep = 4;
        setStep(4);
      }
    } else {
      const expectedBind = expectedLean.bind_type.replace(/_/g, ' ').toUpperCase();
      showOhlecToast(`Incorrect log! Look for the log with ${expectedBind}.`, false);
    }
  }

  private highlightMeshGreen(object: THREE.Object3D) {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (!child.userData.originalMaterial) {
          child.userData.originalMaterial = child.material;
        }

        const applyGreenToMaterial = (mat: THREE.Material) => {
          const newMat = mat.clone();
          if ('color' in newMat) {
            (newMat as any).color.setHex(LOG_HIGHLIGHT_COLOR);
          }
          if ('emissive' in newMat) {
            (newMat as any).emissive.setHex(LOG_HIGHLIGHT_EMISSIVE);
          }
          return newMat;
        };

        if (Array.isArray(child.material)) {
          child.material = child.material.map(applyGreenToMaterial);
        } else if (child.material) {
          child.material = applyGreenToMaterial(child.material);
        }
      }
    });
  }

  private clearLogHighlights() {
    if (!this.entityManager) return;

    for (const ent of this.entityManager.getAll()) {
      if (ent.type === 'log' && ent.mesh) {
        ent.mesh.traverse((child) => {
          if (child instanceof THREE.Mesh && child.userData.originalMaterial) {
            child.material = child.userData.originalMaterial;
            delete child.userData.originalMaterial;
          }
        });
      }
    }
  }

  private triggerShake(object: THREE.Object3D) {
    const existing = this.activeShakes.find((s) => s.object === object);
    if (existing) {
      existing.elapsed = 0;
      return;
    }

    this.activeShakes.push({
      object,
      initialPosition: object.position.clone(),
      elapsed: 0,
      duration: HAZARD_SHAKE_DURATION,
    });
  }

  private highlightMeshRed(object: THREE.Object3D) {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const applyRedToMaterial = (mat: THREE.Material) => {
          const newMat = mat.clone();
          if ('color' in newMat) {
            (newMat as any).color.setHex(HAZARD_HIGHLIGHT_COLOR);
          }
          if ('emissive' in newMat) {
            (newMat as any).emissive.setHex(HAZARD_EMISSIVE_COLOR);
          }
          return newMat;
        };

        if (Array.isArray(child.material)) {
          child.material = child.material.map(applyRedToMaterial);
        } else if (child.material) {
          child.material = applyRedToMaterial(child.material);
        }
      }
    });
  }

  public update(deltaTime: number) {
    if (!this.active || this.activeShakes.length === 0) return;

    for (let i = this.activeShakes.length - 1; i >= 0; i--) {
      const shake = this.activeShakes[i];
      shake.elapsed += deltaTime;
      const progress = shake.elapsed / shake.duration;

      if (progress >= 1.0) {
        shake.object.position.copy(shake.initialPosition);
        this.activeShakes.splice(i, 1);
      } else {
        const decay = 1.0 - progress;
        const offset = Math.sin(shake.elapsed * HAZARD_SHAKE_FREQUENCY) * HAZARD_SHAKE_AMPLITUDE * decay;
        shake.object.position.x = shake.initialPosition.x + offset;
        shake.object.position.z = shake.initialPosition.z + offset * 0.5;
      }
    }
  }

  private validateObjectiveClick(x: number, y: number, z: number) {
    if (this.currentObjectiveIndex >= this.objectives.length) return;

    const activeObj = this.objectives[this.currentObjectiveIndex];
    const target = activeObj.target_zone;

    const dx = x - target.x;
    const dz = z - target.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    if (distance <= target.radius) {
      // SUCCESS
      this.placeTargetMarker(x, y, z, target.radius);
      showOhlecToast(`Objective ${this.currentObjectiveIndex + 1}/${this.objectives.length} set!`, true);

      this.currentObjectiveIndex++;

      if (this.currentObjectiveIndex < this.objectives.length) {
        // Advance to next objective in sequence
        this.syncHUDWithCurrentObjective();
      } else {
        // Step 1 Completed!
        showOhlecToast('Step 1 Complete! All drop zones defined.', true);
        this.currentStep = 2;
        setStep(2);
        if (this.previewReticle) {
          this.previewReticle.visible = false;
        }
      }
    } else {
      // FAILURE
      showOhlecToast('Clicked outside designated drop zone. Re-assess and try again!', false);
      if (this.previewReticle) {
        const mat = this.previewReticle.material as THREE.MeshBasicMaterial;
        mat.color.setHex(RETICLE_COLOR_INVALID);
        setTimeout(() => {
          if (mat) mat.color.setHex(RETICLE_COLOR_NEUTRAL);
        }, 600);
      }
    }
  }

  private placeTargetMarker(x: number, y: number, z: number, radius: number) {
    const group = new THREE.Group();
    group.position.set(x, y + GROUND_Y_OFFSET, z);

    // Inner filled circle decal
    const circleGeo = new THREE.CircleGeometry(radius, 32);
    circleGeo.rotateX(-Math.PI / 2);
    const circleMat = new THREE.MeshBasicMaterial({
      color: RETICLE_COLOR_VALID,
      transparent: true,
      opacity: PLACED_MARKER_OPACITY,
      side: THREE.DoubleSide,
    });
    const circleMesh = new THREE.Mesh(circleGeo, circleMat);
    group.add(circleMesh);

    // Outer border ring decal
    const ringGeo = new THREE.RingGeometry(radius * 0.92, radius, 32);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
    });
    const ringMesh = new THREE.Mesh(ringGeo, ringMat);
    group.add(ringMesh);

    this.renderer.scene.add(group);
    this.placedMarkers.push(group as any);
  }

  public dispose() {
    this.active = false;

    if (this.domElement) {
      this.domElement.removeEventListener('pointermove', this.boundPointerMove);
      this.domElement.removeEventListener('pointerdown', this.boundPointerDown);
      this.domElement = null;
    }

    if (this.previewReticle) {
      this.renderer.scene.remove(this.previewReticle);
      if (this.previewReticle.geometry) this.previewReticle.geometry.dispose();
      if (this.previewReticle.material) {
        (this.previewReticle.material as THREE.Material).dispose();
      }
      this.previewReticle = null;
    }

    for (const marker of this.placedMarkers) {
      this.renderer.scene.remove(marker);
      marker.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        }
      });
    }
    this.placedMarkers = [];

    for (const shake of this.activeShakes) {
      shake.object.position.copy(shake.initialPosition);
    }
    this.activeShakes = [];

    this.clearLogHighlights();
  }
}
